import { privacyAllows } from './privacyRuntime'
import { safeIds } from './analyticsVendors'

export type MetaEvent = 'PageView' | 'ViewContent' | 'AddToCart' | 'InitiateCheckout' | 'Lead' | 'Purchase'
/** Meta also captures referrer; receipt links must never leave the browser. */
export function metaUrlAllowed(): boolean {
  if (typeof window === 'undefined') return false
  const safe = (raw: string) => {
    try {
      const url = new URL(raw)
      return !/^\/(checkout\/success|booking|waiver|quote|order|account|preview|r)(\/|$)/.test(url.pathname)
        && !['key', 'checkout_key', 'session_id', 'token', 'access_token'].some(key => url.searchParams.has(key))
    } catch { return false }
  }
  return safe(window.location.href) && (!document.referrer || safe(document.referrer))
}

type Pending = { name: MetaEvent; payload: Record<string, unknown>; eventId?: string }
let pixelId: string | null = null
let pending: Pending[] = []
const sent = new Set<string>()

function configured(): boolean {
  const raw = document.documentElement.getAttribute('data-sf-privacy')
  try { return Boolean(raw && JSON.parse(raw).vendors.includes('advertising')) } catch { return false }
}

/** Drop pre-consent events. Queue only the permitted initialization race. */
export function trackMetaEvent(name: MetaEvent, payload: Record<string, unknown> = {}, eventId?: string): void {
  if (typeof window === 'undefined' || !privacyAllows('advertising')) { pending = []; return }
  if (!metaUrlAllowed()) { revokeMetaTracking(); return }
  if (!configured()) return
  if (!pixelId || typeof window.fbq !== 'function') {
    if (pending.length < 30) pending.push({ name, payload, eventId })
    return
  }
  const key = eventId ? `sifly_meta:${pixelId}:${name}:${eventId}` : null
  if (key) {
    if (sent.has(key)) return
    try { if (sessionStorage.getItem(key)) return } catch { /* storage may be unavailable */ }
  }
  window.fbq('trackSingle', pixelId, name, payload, ...(eventId ? [{ eventID: eventId }] : []))
  if (key) {
    sent.add(key)
    try { sessionStorage.setItem(key, '1') } catch { /* in-memory dedupe remains */ }
  }
}

export function initializeMetaTracking(id: string): void {
  pixelId = safeIds({ ga4Id: null, metaPixelId: id, clarityId: null }).advertising
  const queued = pending
  pending = []
  for (const event of queued) trackMetaEvent(event.name, event.payload, event.eventId)
}

export function revokeMetaTracking(): void {
  pending = []; pixelId = null
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') window.fbq('consent', 'revoke')
}
